import React, { FC, useState } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import {
  CarouselRoot,
  CarouselControls,
  CarouselState,
  CarouselPreviousButton,
  CarouselNextButton,
  UnstyledCarouselScrollArea,
  Flex,
  Box,
} from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { useFindLocaleText } from '../../text-localisation'
import { useSearchStore } from '../../store/SearchStore'
import {
  FiltersCarouselStickyWrapper,
  CarouselItem,
  FiltersMenuButton,
} from './components'
import { createCarouselFilterItems } from './createCarouselFilterItems'
import { useNbaFilters } from '../../utils/nbaFilters'

/* istanbul ignore next */
const StyledFlex = styled(Flex)<{
  paddingLeft: boolean
  noBorderBottom?: boolean
}>`
  ${({ paddingLeft, noBorderBottom }) =>
    s({
      width: { xs: '100vw', md: '100%' },
      maxWidth: '100%',
      flexDirection: 'column',
      py: { xs: 3, md: 4 },
      mb: 0,
      pl: paddingLeft ? { xs: 6, md: 8, lg: 10, xxl: 12 } : 0,
      pr: 0,
      bgcolor: 'colorBackground01',
      borderBottom: noBorderBottom ? 0 : 1,
      borderColor: 'colorBorder03',
    })}
`

const StyledCarouselRoot = styled(CarouselRoot)`
  ${s({
    px: 0,
  })}
`

const StyledCarouselScrollArea = styled(UnstyledCarouselScrollArea)`
  gap: ${spacingPx(4)};
`

export const FiltersCarousel: FC<{ shouldBeSticky?: boolean }> = ({
  shouldBeSticky = true,
}) => {
  const filtersfromStore = useSearchStore(store => store.filters)
  const filters = useNbaFilters(filtersfromStore || [])

  const localiseText = useFindLocaleText()
  const [showFiltersButton, setShowFiltersButton] = useState(false)

  const toggleFiltersMenu = useSearchStore(store => store.toggleFiltersMenu)
  const filtersMenuOpen = useSearchStore(store => store.filtersMenuOpen)
  const resultsLoading = useSearchStore(store => store.loading)
  const selectedFilters = useSearchStore(store => store.selectedFilters)
  const optimisticRender = useSearchStore(store => store.optimisticRender)

  const carouselItems: React.ReactNode[] = []

  if (showFiltersButton) {
    carouselItems.push(
      <CarouselItem key="open-filters-button">
        <FiltersMenuButton
          toggleMenu={toggleFiltersMenu}
          isMenuOpen={filtersMenuOpen}
          appliedFilters={selectedFilters}
        />
      </CarouselItem>,
    )
  }

  carouselItems.push(
    ...createCarouselFilterItems({
      filters,
      resultsLoading,
      selectedFilters,
      optimisticRender,
      localiseText,
    }),
  )

  return shouldBeSticky ? (
    <FiltersCarouselStickyWrapper
      onStickyChange={sticky => setShowFiltersButton(sticky)}
    >
      <StyledFlex
        data-testid="filters-carousel"
        paddingLeft={!showFiltersButton}
      >
        {carouselItems.length > 0 ? (
          <CarouselState>
            <StyledCarouselRoot aria-label={localiseText('find.nba_filters')}>
              <Box display={{ xs: 'none', md: 'block' }}>
                <CarouselControls>
                  <CarouselPreviousButton />
                  <CarouselNextButton />
                </CarouselControls>
              </Box>
              <StyledCarouselScrollArea>
                {carouselItems}
              </StyledCarouselScrollArea>
            </StyledCarouselRoot>
          </CarouselState>
        ) : /* istanbul ignore next */ null}
      </StyledFlex>
    </FiltersCarouselStickyWrapper>
  ) : (
    <StyledFlex
      data-testid="filters-carousel"
      paddingLeft={!showFiltersButton}
      noBorderBottom
    >
      {carouselItems.length > 0 ? (
        <CarouselState>
          <StyledCarouselRoot aria-label={localiseText('find.nba_filters')}>
            <Box display={{ xs: 'none', md: 'block' }}>
              <CarouselControls>
                <CarouselPreviousButton />
                <CarouselNextButton />
              </CarouselControls>
            </Box>
            <StyledCarouselScrollArea>{carouselItems}</StyledCarouselScrollArea>
          </StyledCarouselRoot>
        </CarouselState>
      ) : /* istanbul ignore next */ null}
    </StyledFlex>
  )
}
